import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { handleUserData, logout } from "../auth/authSlice";
import Popup from "../features/EventOrganiser/Popup";
import MyReferrals from "./MyReferrals";
import MyInvoices from "./MyInvoices";
import PasswordInput from "./PasswordInput";
import TrialExpiryAlert from "../components/TrialExpiryAlert";
import { BASE_URL } from "../configs/WebServiceUrl";
import userStore from "../zustand/userStore";
import axios from "axios";
import { lmsGetCoachingToken } from "../features/lms/api/lmsApi";

export default function Profile() {
  const navigate = useNavigate();
  const setUser = userStore.getState().setUser;
  const [hoveredButton, setHoveredButton] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  const [showMyReferrals, setShowMyReferrals] = useState(false);
  const [showMyInvoices, setShowMyInvoices] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const { user } = useSelector((state) => state.authSlice);
  const dispatch = useDispatch();

  const [localUser, setLocalUser] = useState(null);
  const [schoolName, setSchoolName] = useState("");
  const [address, setAddress] = useState("");

  const fetchUserDetails = async () => {
    if (!user?.admin_id) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/api/profile/school-user-details/${user.admin_id}`,
      );
      const data = await response.json();
      if (response.ok) {
        const userData = data.user || data;
        setLocalUser(userData);
        setSchoolName(userData.school_name || "");
        setAddress(userData.address || "");

        // Handle various image property names
        const img = userData.image_url || "";
        if (img) {
          setProfileImage(img.startsWith("http") ? img : `${BASE_URL}/${img}`);
        }
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, [user?.admin_id]);

  // Track window width for responsive styles
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Responsive breakpoints
  const isMobile = windowWidth <= 490;
  const isTablet = windowWidth > 490 && windowWidth <= 768;
  const isSmallTablet = windowWidth > 768 && windowWidth <= 991;

  const handleUpdateProfile = async () => {
    if (!schoolName || !address) {
      alert("School name and address are required");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/profile/update_school_details`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: user.admin_id,
          school_name: schoolName,
          address: address,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        alert("Profile updated successfully");
        const userData = JSON.parse(localStorage.getItem("userData"));
        if (userData) {
          userData.school_name = schoolName;
          userData.address = address;
          localStorage.setItem("userData", JSON.stringify(userData));
          setUser({
            ...userData,
            school_name: schoolName,
            address: address,
          });
        }
        fetchUserDetails();
      } else {
        alert(data.message || "Failed to update profile");
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      alert("Both fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const resp = await axios.post(`${BASE_URL}/api/profile/update-password`, {
        admin_id: user.admin_id,
        password: newPassword,
      }, { headers: { "Content-Type": "application/json" } });
      const data = resp?.data;
      if (resp.status >= 200 && resp.status < 300) {
        alert("Password updated successfully");
        try {
          await dispatch(logout()).unwrap();
        } catch (logoutError) {
          console.error("Logout after password reset failed:", logoutError);
        } finally {
          window.location.replace("/login");
        }
      } else {
        alert(data.message || "Failed to update password");
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setLoading(true);
      const formData = new FormData();
      formData.append("id", user?.admin_id);
      formData.append("image", file);

      try {
        const response = await fetch(`${BASE_URL}/api/profile/update-school-image`, {
          method: "POST",
          body: formData,
        });

        const data = await response.json();
        if (response.ok) {
          alert("Profile picture updated successfully");
          // If the API returns the image_url directly, we can use it
          const newImg = data.image_url;
          if (newImg) {
            setProfileImage(newImg);
          }
          fetchUserDetails();
        } else {
          alert(data.message || "Failed to update profile picture");
        }
      } catch (error) {
        console.error(error);
        alert("Something went wrong updating image");
      } finally {
        setLoading(false);
      }
    }
    setShowUploadOptions(false);
  };

  const handleGalleryClick = () => {
    fileInputRef.current.click();
  };

  const handleCameraClick = () => {
    cameraInputRef.current.click();
  };

  const getPlanExpiry = (payment, userCreatedAt) => {
    if (payment?.created_at) {
      const expiry = new Date(payment.created_at);
      expiry.setFullYear(expiry.getFullYear() + 1);
      return expiry.toLocaleDateString("en-GB");
    }
    if (userCreatedAt) {
      const trialExpiry = new Date(userCreatedAt);
      trialExpiry.setDate(trialExpiry.getDate() + 7);
      return trialExpiry.toLocaleDateString("en-GB");
    }

    return "";
  };
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Copied to clipboard");
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const getReferralUrl = (code) => {
    if (!code) return "";
    return `https://mydocmaster.com/registration?ref=${code}`;
  };

  const [coachingToken, setCoachingToken] = useState("");

  const currentPayments = user?.payments || [];
  const planType = (currentPayments?.[0]?.plan_type || "").toLowerCase();
  const isCoachingPlan = planType === "coaching";

  const activeUserId = user?.admin_id || user?.id || localUser?.admin_id || localUser?.id || "";
  const activeSchoolName = (schoolName || localUser?.school_name || user?.school_name || "coaching").trim();

  useEffect(() => {
    const fetchSignedCoachingToken = async () => {
      if (!activeUserId) return;
      try {
        const res = await lmsGetCoachingToken(activeUserId);
        if (res.data?.success && res.data?.token) {
          setCoachingToken(res.data.token);
        }
      } catch (e) {
        console.error("Error fetching coaching token:", e);
      }
    };

    if (activeUserId) {
      fetchSignedCoachingToken();
    }
  }, [activeUserId]);

  const coachingPortalUrl = `/${coachingToken || ""}`;
  const fullCoachingUrl = `https://mydocmaster.com/${coachingToken || ""}`;

  return (
    <div
      style={{
        fontFamily:
          '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
        minHeight: "100vh",
        padding: isMobile ? "0px 16px 16px" : isTablet ? "0px 24px 24px" : "0px 48px 48px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflowX: "hidden",
      }}
    >
      <style>{`
        body {
          overflow: hidden;
          margin: 0;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .back-button:hover {
          background-color: #f1f5f9 !important;
          transform: scale(1.05);
        }
      `}</style>

      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              marginBottom: "16px",
            }}
          />
          <p style={{ color: "#1e293b", fontWeight: "600", margin: 0 }}>
            Processing...
          </p>
        </div>
      )}

      {/* Header Container */}
      <div
        style={{
          width: "100%",
          maxWidth: "1000px",
          padding: isMobile ? "5px 0" : "10px 0",
          display: "flex",
          alignItems: "center",
          gap: "20px",
          position: "relative",
          marginBottom: isMobile ? "2px" : "4px",
        }}
      >
        <button
          onClick={() => navigate("/")}
          className="back-button"
          style={{
            width: isMobile ? "32px" : "40px",
            height: isMobile ? "32px" : "40px",
            borderRadius: "50%",
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            transition: "all 0.2s ease",
            padding: "0",
            flexShrink: 0,
          }}
        >
          <svg
            width={isMobile ? "16" : "20"}
            height={isMobile ? "16" : "20"}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1e3a8a"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
        </button>

        <div style={{ flex: 1, textAlign: "center", marginRight: isMobile ? "32px" : "40px" }}>
          <h1
            style={{
              fontSize: isMobile ? "20px" : isTablet ? "24px" : "28px",
              fontWeight: "800",
              background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              margin: "0",
              letterSpacing: "-0.5px",
            }}
          >
            Profile Details
          </h1>
          <p
            style={{
              fontSize: isMobile ? "11px" : "13px",
              color: "#64748b",
              margin: "2px 0 0 0",
              fontWeight: "400",
            }}
          >
            Manage your personal and professional information
          </p>
        </div>
      </div>

      {/* Main Content Container */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile || isTablet ? "column-reverse" : "row",
          gap: isMobile ? "12px" : "24px",
          alignItems: isMobile || isTablet ? "center" : "flex-start",
          justifyContent: "center",
          width: "100%",
          maxWidth: "1000px",
          margin: "0 auto",
          maxHeight: "calc(100vh - 120px)",
        }}
      >
        {/* Left Form Card */}
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: isMobile ? "12px" : "20px",
            padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
            boxShadow:
              "0 20px 60px rgba(0, 0, 0, 0.08), 0 8px 20px rgba(0, 0, 0, 0.04)",
            flex: "1",
            width: isMobile || isTablet ? "100%" : "auto",
            maxWidth: "540px",
            border: "1px solid rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(20px)",
            boxSizing: "border-box",
            overflowY: "auto",
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          <style>{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          {/* Personal Information Section */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: isMobile ? "20px" : "28px",
            }}
          >
            <div
              style={{
                width: "4px",
                height: "24px",
                background: "linear-gradient(180deg, #3b82f6 0%, #1e40af 100%)",
                borderRadius: "2px",
              }}
            ></div>
            <h2
              style={{
                fontSize: isMobile ? "16px" : "18px",
                fontWeight: "700",
                color: "#0f172a",
                margin: "0",
                letterSpacing: "-0.3px",
              }}
            >
              Personal Information
            </h2>
          </div>

          {/* Full Name and Email Row */}
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? "16px" : "20px",
              marginBottom: isMobile ? "16px" : "20px",
            }}
          >
            {/* Full Name */}
            <div style={{ flex: "1" }}>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: "#475569",
                  display: "block",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Full Name
                <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Enter your full name"
                value={localUser?.admin_name || ""}
                readOnly
                style={{
                  width: "100%",
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  fontSize: isMobile ? "13px" : "14px",
                  color: "#1e293b",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.3s ease",
                  fontWeight: "500",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            {/* Email ID */}
            <div style={{ flex: "1" }}>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: "#475569",
                  display: "block",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Email ID
                <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
              </label>
              <input
                type="email"
                placeholder="Enter your email ID"
                value={localUser?.email || ""}
                readOnly
                style={{
                  width: "100%",
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  fontSize: isMobile ? "13px" : "14px",
                  color: "#1e293b",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.3s ease",
                  fontWeight: "500",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          {/* Mobile Number and Type of User Row */}
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? "16px" : "20px",
              marginBottom: isMobile ? "20px" : "28px",
            }}
          >
            {/* Mobile Number */}
            <div style={{ flex: "1" }}>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: "#475569",
                  display: "block",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Mobile Number
                <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Enter your mobile number"
                value={localUser?.contact_number || ""}
                readOnly
                style={{
                  width: "100%",
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  fontSize: isMobile ? "13px" : "14px",
                  color: "#1e293b",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.3s ease",
                  fontWeight: "500",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            {/* Referral Code */}
            <div style={{ flex: "1" }}>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: "#475569",
                  display: "block",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Referral Code
              </label>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <input
                  type="text"
                  value={localUser?.referral_code || ""}
                  disabled
                  style={{
                    flex: 1,
                    padding: isMobile ? "10px 12px" : "12px 16px",
                    fontSize: isMobile ? "13px" : "14px",
                    color: "#64748b",
                    backgroundColor: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    boxSizing: "border-box",
                    fontWeight: "500",
                    cursor: "not-allowed",
                  }}
                />

                {/* Copy Code Icon */}
                <i
                  className="fa fa-copy"
                  title="Copy referral code"
                  onClick={() =>
                    copyToClipboard(
                      localUser?.referral_code || "",
                    )
                  }
                  style={{
                    fontSize: "16px",
                    color: "#2563eb",
                    cursor: "pointer",
                  }}
                />

                {/* Copy URL Icon */}
                <i
                  className="fa fa-link"
                  title="Copy referral link"
                  onClick={() =>
                    copyToClipboard(
                      getReferralUrl(
                        localUser?.referral_code || "",
                      ),
                    )
                  }
                  style={{
                    fontSize: "16px",
                    color: "#16a34a",
                    cursor: "pointer",
                  }}
                />
              </div>
            </div>
          </div>
          {/* Referral Code */}

          {/* Student Registration Share Link - ONLY shown when planType === 'coaching' */}
          {isCoachingPlan && (
            <div style={{ marginBottom: isMobile ? "20px" : "28px" }}>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: "#475569",
                  display: "block",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Student Registration Link
              </label>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <input
                  type="text"
                  value={fullCoachingUrl}
                  disabled
                  style={{
                    flex: 1,
                    padding: isMobile ? "10px 12px" : "12px 16px",
                    fontSize: isMobile ? "13px" : "14px",
                    color: "#64748b",
                    backgroundColor: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    boxSizing: "border-box",
                    fontWeight: "500",
                    cursor: "not-allowed",
                  }}
                />

                {/* Copy Code Icon */}
                <i
                  className="fa fa-copy"
                  title="Copy student registration link"
                  onClick={() => copyToClipboard(fullCoachingUrl)}
                  style={{
                    fontSize: "16px",
                    color: "#2563eb",
                    cursor: "pointer",
                  }}
                />

                {/* Copy URL Icon */}
                <i
                  className="fa fa-link"
                  title="Copy student registration link"
                  onClick={() => copyToClipboard(fullCoachingUrl)}
                  style={{
                    fontSize: "16px",
                    color: "#16a34a",
                    cursor: "pointer",
                  }}
                />
              </div>
            </div>
          )}


          {/* Other Details Section */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: isMobile ? "20px" : "28px",
            }}
          >
            <div
              style={{
                width: "4px",
                height: "24px",
                background: "linear-gradient(180deg, #3b82f6 0%, #1e40af 100%)",
                borderRadius: "2px",
              }}
            ></div>
            <h2
              style={{
                fontSize: isMobile ? "16px" : "18px",
                fontWeight: "700",
                color: "#0f172a",
                margin: "0",
                letterSpacing: "-0.3px",
              }}
            >
              Other Details
            </h2>
          </div>

          {/* School Name */}
          <div style={{ marginBottom: isMobile ? "16px" : "20px" }}>
            <label
              style={{
                fontSize: "11px",
                fontWeight: "600",
                color: "#475569",
                display: "block",
                marginBottom: "8px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              School Name
              <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
            </label>
            <input
              type="text"
              placeholder="Enter your school name"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              style={{
                width: "100%",
                padding: isMobile ? "10px 12px" : "12px 16px",
                fontSize: isMobile ? "13px" : "14px",
                color: "#1e293b",
                backgroundColor: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                boxSizing: "border-box",
                outline: "none",
                transition: "all 0.3s ease",
                fontWeight: "500",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e2e8f0";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Address */}
          <div style={{ marginBottom: isMobile ? "24px" : "32px" }}>
            <label
              style={{
                fontSize: "11px",
                fontWeight: "600",
                color: "#475569",
                display: "block",
                marginBottom: "8px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Address
              <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
            </label>
            <textarea
              placeholder="Enter your address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              style={{
                width: "100%",
                padding: isMobile ? "10px 12px" : "12px 16px",
                fontSize: isMobile ? "13px" : "14px",
                color: "#1e293b",
                backgroundColor: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                boxSizing: "border-box",
                outline: "none",
                resize: "none",
                minHeight: isMobile ? "70px" : "80px",
                fontFamily: "inherit",
                lineHeight: "1.6",
                transition: "all 0.3s ease",
                fontWeight: "500",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e2e8f0";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Buttons */}
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "center",
              gap: isMobile ? "12px" : "16px",
            }}
          >
            <button
              onClick={() => setShowResetModal(true)}
              style={{
                padding: isMobile ? "10px 20px" : "12px 28px",
                fontSize: isMobile ? "13px" : "14px",
                fontWeight: "600",
                color: hoveredButton === "reset" ? "#1e40af" : "#475569",
                backgroundColor:
                  hoveredButton === "reset" ? "#eff6ff" : "#ffffff",
                border: "1px solid",
                borderColor: hoveredButton === "reset" ? "#3b82f6" : "#e2e8f0",
                borderRadius: "10px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  hoveredButton === "reset"
                    ? "translateY(-2px)"
                    : "translateY(0)",
                boxShadow:
                  hoveredButton === "reset"
                    ? "0 8px 20px rgba(59, 130, 246, 0.15)"
                    : "none",
                width: isMobile ? "100%" : "auto",
              }}
              onMouseEnter={() => setHoveredButton("reset")}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Reset Password
            </button>
            <button
              onClick={handleUpdateProfile}
              style={{
                padding: isMobile ? "10px 20px" : "12px 28px",
                fontSize: isMobile ? "13px" : "14px",
                fontWeight: "600",
                color: "#ffffff",
                background:
                  hoveredButton === "save"
                    ? "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)"
                    : "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  hoveredButton === "save"
                    ? "translateY(-2px)"
                    : "translateY(0)",
                boxShadow:
                  hoveredButton === "save"
                    ? "0 12px 24px rgba(37, 99, 235, 0.4)"
                    : "0 4px 12px rgba(37, 99, 235, 0.25)",
                width: isMobile ? "100%" : "auto",
              }}
              onMouseEnter={() => setHoveredButton("save")}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Save Changes
            </button>
          </div>
        </div>

        {/* Right Side - Profile Picture Upload and Subscription */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile || isTablet ? "row" : "column",
            gap: isMobile ? "16px" : "24px",
            alignItems: "center",
            justifyContent: "center",
            width: isMobile || isTablet ? "100%" : "auto",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          {/* Profile Picture Upload */}
          <div
            style={{
              position: "relative",
              width: isMobile ? "120px" : "160px",
              height: isMobile ? "120px" : "160px",
            }}
          >
            {/* Hidden file inputs */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              style={{ display: "none" }}
            />
            <input
              type="file"
              ref={cameraInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              capture="user"
              style={{ display: "none" }}
            />

            {/* Gradient border */}
            <div
              style={{
                position: "absolute",
                inset: "-4px",
                background:
                  "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)",
                borderRadius: "50%",
              }}
            ></div>

            {/* Image container */}
            <div
              onClick={() => setShowUploadOptions(!showUploadOptions)}
              style={{
                position: "relative",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                overflow: "hidden",
                border: "4px solid #ffffff",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
                cursor: "pointer",
                backgroundColor: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#94a3b8",
                  }}
                >
                  <svg
                    width={isMobile ? "32" : "40"}
                    height={isMobile ? "32" : "40"}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <span
                    style={{
                      fontSize: isMobile ? "9px" : "10px",
                      marginTop: "4px",
                      fontWeight: "500",
                    }}
                  >
                    Upload
                  </span>
                </div>
              )}

              {/* Hover overlay */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0,
                  transition: "opacity 0.3s ease",
                  borderRadius: "50%",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </div>
            </div>

            {/* Upload options dropdown */}
            {showUploadOptions && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  marginTop: "12px",
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
                  overflow: "hidden",
                  zIndex: 10,
                  minWidth: isMobile ? "140px" : "160px",
                }}
              >
                <div
                  onClick={handleCameraClick}
                  style={{
                    padding: isMobile ? "10px 12px" : "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f8fafc")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#ffffff")
                  }
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  <span
                    style={{
                      fontSize: isMobile ? "12px" : "14px",
                      fontWeight: "500",
                      color: "#1e293b",
                    }}
                  >
                    Take Photo
                  </span>
                </div>
                <div
                  onClick={handleGalleryClick}
                  style={{
                    padding: isMobile ? "10px 12px" : "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f8fafc")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#ffffff")
                  }
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span
                    style={{
                      fontSize: isMobile ? "12px" : "14px",
                      fontWeight: "500",
                      color: "#1e293b",
                    }}
                  >
                    Gallery
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Subscription Details Card */}
          <div
            style={{
              background:
                "linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)",
              borderRadius: isMobile ? "16px" : "20px",
              padding: isMobile ? "20px" : "28px",
              color: "#ffffff",
              width: isMobile
                ? "calc(100% - 140px)"
                : isTablet
                  ? "240px"
                  : "280px",
              minWidth: isMobile ? "180px" : "auto",
              boxShadow: "0 20px 40px rgba(37, 99, 235, 0.3)",
              position: "relative",
              overflow: "hidden",
              flex: isMobile ? "1" : "none",
            }}
          >
            {/* Background decoration */}
            <div
              style={{
                position: "absolute",
                top: "-60px",
                right: "-60px",
                width: "180px",
                height: "180px",
                background:
                  "radial-gradient(circle at top right, rgba(255,255,255,0.35), rgba(255,255,255,0) 70%)",
                borderRadius: "50%",
                filter: "blur(10px)",
                pointerEvents: "none",
              }}
            ></div>

            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: isMobile ? "12px" : "16px",
                position: "relative",
              }}
            >
              <h3
                style={{
                  fontSize: isMobile ? "12px" : "14px",
                  fontWeight: "700",
                  margin: "0",
                  letterSpacing: "-0.3px",
                  lineHeight: "1.3",
                }}
              >
                Subscription
                <br />
                Details
              </h3>
            </div>

            {/* Current Plan */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: "10px",
                padding: isMobile ? "10px" : "12px",
                marginBottom: isMobile ? "8px" : "10px",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <p
                style={{
                  fontSize: isMobile ? "8px" : "9px",
                  color: "rgba(255, 255, 255, 0.7)",
                  margin: "0 0 4px 0",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  fontWeight: "600",
                }}
              >
                Current Plan
              </p>
              <p
                style={{
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: "700",
                  margin: "0",
                  letterSpacing: "-0.3px",
                }}
              >
                {localUser?.payments?.[0]?.plan_type
                  ? localUser?.payments?.[0]?.plan_type
                  : "Trail"}
              </p>
            </div>

            {/* Expiry Date */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: "10px",
                padding: isMobile ? "10px" : "12px",
                marginBottom: isMobile ? "12px" : "16px",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <p
                style={{
                  fontSize: isMobile ? "8px" : "9px",
                  color: "rgba(255, 255, 255, 0.7)",
                  margin: "0 0 4px 0",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  fontWeight: "600",
                }}
              >
                Expiry Date
              </p>
              <p
                style={{
                  fontSize: isMobile ? "12px" : "14px",
                  fontWeight: "700",
                  margin: "0",
                  color: "#fde047",
                  letterSpacing: "-0.3px",
                }}
              >
                {/* December 31, 2024 */}
                {getPlanExpiry(
                  localUser?.payments?.[0] || user?.payments?.[0],
                  localUser?.created || user?.created,
                )}
              </p>
            </div>

            {/* Upgrade Button */}
            <button
              style={{
                width: "100%",
                padding: isMobile ? "8px" : "10px",
                fontSize: isMobile ? "11px" : "13px",
                fontWeight: "700",
                color: "#1e40af",
                backgroundColor:
                  hoveredButton === "upgrade" ? "#f0f9ff" : "#ffffff",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                transition: "all 0.3s ease",
                transform:
                  hoveredButton === "upgrade" ? "scale(1.02)" : "scale(1)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              }}
              onMouseEnter={() => setHoveredButton("upgrade")}
              onMouseLeave={() => setHoveredButton(null)}
            >
              <svg
                width={isMobile ? "12" : "14"}
                height={isMobile ? "12" : "14"}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
              Upgrade Now
            </button>
            <button
              style={{
                width: "100%",
                marginTop: "10px",
                padding: isMobile ? "8px" : "10px",
                fontSize: isMobile ? "11px" : "13px",
                fontWeight: "700",
                color: "#ffffff",
                background: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(34, 197, 94, 0.35)",
              }}
              onClick={() => setShowMyReferrals(true)}
            >
              My Referrals
            </button>
            {(currentPayments?.length > 0) && (
              <button
                style={{
                  width: "100%",
                  marginTop: "10px",
                  padding: isMobile ? "8px" : "10px",
                  fontSize: isMobile ? "11px" : "13px",
                  fontWeight: "700",
                  color: "#ffffff",
                  background: "linear-gradient(135deg, #0284c7 0%, #0ea5e9 100%)",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(14, 165, 233, 0.35)",
                }}
                onClick={() => setShowMyInvoices(true)}
              >
                View Invoices
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Reset Password Modal */}
      {showResetModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: isMobile ? "16px" : "24px",
          }}
          onClick={() => setShowResetModal(false)}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              padding: isMobile ? "24px" : "32px",
              width: "100%",
              maxWidth: isMobile ? "100%" : "400px",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowResetModal(false)}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                color: "#94a3b8",
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {/* Modal Title */}
            <h3
              style={{
                fontSize: isMobile ? "16px" : "18px",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 24px 0",
                textAlign: "center",
              }}
            >
              Reset Password<span style={{ color: "#ef4444" }}>*</span>
            </h3>

            <div
              style={{
                margin: "0 0 20px 0",
                padding: "12px 14px",
                backgroundColor: "#fff7ed",
                border: "1px solid #fed7aa",
                borderRadius: "8px",
                color: "#9a3412",
                fontSize: isMobile ? "12px" : "13px",
                lineHeight: "1.5",
              }}
            >
              Changing your password will log you out from all devices.
            </div>

            {/* Password Input */}
            {/* <div style={{ marginBottom: "24px" }}>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="************"
                style={{
                  width: "100%",
                  padding: isMobile ? "12px 14px" : "14px 16px",
                  fontSize: "14px",
                  color: "#1e293b",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.3s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div> */}
            {/* New Password */}
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New Password"
              show={showNewPassword}
              toggleShow={() => setShowNewPassword(!showNewPassword)}
            />

            <PasswordInput
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm Password"
              show={showConfirmPassword}
              toggleShow={() => setShowConfirmPassword(!showConfirmPassword)}
            />

            {/* Reset Button */}
            <div
              style={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
              }}
            >
              <button
                onClick={handleResetPassword}
                style={{
                  padding: "12px 24px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#ffffff",
                  background:
                    "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)",
                  width: isMobile ? "100%" : "auto",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 8px 20px rgba(37, 99, 235, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow =
                    "0 4px 12px rgba(37, 99, 235, 0.25)";
                }}
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
      {showMyReferrals && (
        <Popup
          title="My Referrals"
          height="500px"
          maxWidth="1000px"
          onClose={() => setShowMyReferrals(false)}
          minHeight="500px"
        >
          <MyReferrals />
        </Popup>
      )}
      {showMyInvoices && (
        <Popup
          title="My Invoices"
          height="550px"
          maxWidth="1000px"
          onClose={() => setShowMyInvoices(false)}
          minHeight="500px"
        >
          <MyInvoices />
        </Popup>
      )}
    </div>
  );
}
